import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import {
	Button,
	Input,
	SpinnerOverlay,
	SubmissionButtons,
} from '@components/common';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { secondsToHHMMSS } from '@utils/userProfile/secondsToHHMMSS';

import { IResponseError } from '@interfaces/errors/apiErrors';
import { ICode2FaFormValues, ISendCodeForChangePasswordWith2Fa } from '@interfaces/user/userProfile';

import { ESnackbarTypes } from '@enums/common';
import { ECode2FaForm } from '@enums/userProfile';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { regExpNumbers } from '@constants/regExp';
import {
	DESCRIPTION_CODE_FORM,
	ONE_SECOND_IN_MILLISECONDS,
	ZERO,
} from './constants';

import { schema } from './schema';

import styles from './CodeForm.module.scss';

interface ICodeFormProps {
	negativeHandler: () => void;
	getCode: () => void;
	sendForm: (value: ISendCodeForChangePasswordWith2Fa) => void;
	isLoading: boolean;
	leftSeconds?: number;
}

const CodeForm = ({
	negativeHandler,
	getCode,
	sendForm,
	isLoading,
	leftSeconds,
}: ICodeFormProps) => {
	const dispatch = useAppDispatch();

	const [seconds, setSeconds] = useState(ZERO);

	const formattedTime = useMemo(() => secondsToHHMMSS(seconds), [seconds]);

	const isEnableResendCode = seconds === ZERO;

	useEffect(() => {
		setSeconds(!leftSeconds ? ZERO : leftSeconds);
	}, [leftSeconds]);

	useEffect(() => {
		const interval = setInterval(() => {
			setSeconds((prevSeconds) => {
				if (prevSeconds === 0) {
					clearInterval(interval);
				} else return prevSeconds - 1;

				return prevSeconds;
			});
		}, ONE_SECOND_IN_MILLISECONDS);

		return () => clearInterval(interval);
	}, [seconds]);

	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
		setError,
	} = useForm<ICode2FaFormValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(schema),
		defaultValues: {
			[ECode2FaForm.code]: '',
		},
	});

	const setErrorText = (message: string) => {
		setError(ECode2FaForm.code, {
			message,
		});
	};

	const onSubmit = async (values: ICode2FaFormValues) => {
		try {
			await sendForm({ code: values[ECode2FaForm.code] });
		} catch (err: unknown) {
			if (!(err as IResponseError).errors[0].message) return;

			setErrorText((err as IResponseError).errors[0].message);
		}
	};

	const getCodeHandler = async () => {
		try {
			await getCode();

			setSeconds(!leftSeconds ? ZERO : leftSeconds);
		} catch (error) {
			dispatch(setSnackbar({
				message: (error as Error).message,
				type: ESnackbarTypes.error,
			}));
		}
	};

	return (
		<div className={styles.codeFormContainer}>
			{isLoading && <SpinnerOverlay />}
			<div>
				{DESCRIPTION_CODE_FORM}
			</div>
			<div className={styles.codeInput}>
				<Input
					errorMessage={errors?.code?.message}
					label='Verification code'
					name={ECode2FaForm.code}
					placeholder='Enter code'
					regexValidation={regExpNumbers}
					register={register}
					required
					setValue={setValue}
				/>
				<Button
					className={styles.button}
					disabled={!isEnableResendCode || isLoading}
					onClick={getCodeHandler}
					text='Send code'
				/>
			</div>
			<div className={styles.resendCodeText}>
				Resend code available {!isEnableResendCode ? `in ${formattedTime}` : ''}
			</div>
			<SubmissionButtons
				disabledPositive={!!errors?.code?.message}
				negativeHandler={negativeHandler}
				positiveHandler={handleSubmit(onSubmit)}
				positiveLabel='Confirm'
			/>
		</div>
	);
};

export default CodeForm;
